import { Button, Popover, Table, Text } from '@chakra-ui/react';
import { FiHelpCircle } from 'react-icons/fi'
import { explainCopy } from '../../lib/explainCopy'
import { buildLeanBreakdown } from './leanBreakdown'
import type { LeanCyclePoint } from '../../types/geoAnalytics'

interface LeanExplainerProps {
  leanFormatted: string
  leanByCycle: LeanCyclePoint[]
}

const pct = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`)

export function LeanExplainer({ leanFormatted, leanByCycle }: LeanExplainerProps) {
  const b = buildLeanBreakdown(leanByCycle)

  return (
    <Popover.Root lazyMount unmountOnExit positioning={{
      placement: 'bottom-start'
    }}>
      <Popover.Trigger asChild>
        <Button
          size="xs"
          variant='link'
          colorPalette="brand"
          minH="44px"
          aria-label="How is ORCL Lean computed?"><FiHelpCircle />How's this computed?
                  </Button>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content w="340px" maxW="90vw">
          <Popover.Arrow />
          <Popover.Body>
            <Text fontSize="sm" mb={b.rows.length > 0 ? 3 : 0}>
              {explainCopy.leanDefinition()}
            </Text>

            {b.rows.length > 0 && (
              <Table.Root size="sm" variant="line" mb={b.reconciliation ? 3 : 0}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Cycle</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign='end'>District D%</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign='end'>Nation D%</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign='end'>Lean</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {b.rows.map((r) => (
                    <Table.Row key={r.year}>
                      <Table.Cell>{r.year}</Table.Cell>
                      <Table.Cell textAlign='end'>{pct(r.districtDemSharePct)}</Table.Cell>
                      <Table.Cell textAlign='end'>{pct(r.nationalDemSharePct)}</Table.Cell>
                      <Table.Cell fontWeight="semibold" textAlign='end'>{r.cycleLeanFmt}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                {b.rows.length > 1 && (
                  <Table.Footer>
                    <Table.Row>
                      <Table.ColumnHeader>Overall (avg)</Table.ColumnHeader>
                      <Table.ColumnHeader aria-hidden textAlign='end' />
                      <Table.ColumnHeader aria-hidden textAlign='end' />
                      <Table.ColumnHeader textAlign='end'>{leanFormatted}</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Footer>
                )}
              </Table.Root>
            )}

            {b.reconciliation && (
              <Text fontSize="sm" color="gray.600">
                {explainCopy.leanReconciliation(
                  b.reconciliation.rawMarginFmt,
                  b.reconciliation.nationalMarginFmt,
                  b.reconciliation.leanFmt,
                )}
              </Text>
            )}
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
